// @flow
import { ORCH_ENDPOINT_PATH } from '../config';
import { apiTypes as Types } from '../constants';
import { EXIT_TYPE } from '../constants/ApiTypes';
import { type NodeType } from '../components/Node';

type NodeNext = {|
  node_type: Types.DELAY_TYPE | Types.ENTRY_TYPE,
  next: string
|};

type NoNext = {|
  node_type: Types.EXIT_TYPE
|};

type EventNodeNext = {|
  node_type: Types.EVENT_LISTENER_TYPE,
  on_seen: string,
  on_expired: string
|};

type MessageNodeNext = {|
  node_type: Types.MESSAGE_TYPE,
  on_sent: string,
  on_not_sent: string
|};

type Node = NoNext | NodeNext | EventNodeNext | MessageNodeNext;

type ApiNodes = {
  [string]: Node
};

const RELEVANT_NODE_TYPES = [
  Types.DELAY_TYPE,
  Types.EVENT_LISTENER_TYPE,
  Types.MESSAGE_TYPE
];

export const isBranchedNode = (node: NodeType): boolean => {
  const { data: { node_type: nodeType } } = node;
  return nodeType === Types.EVENT_LISTENER_TYPE;
};

export const getNodeChildNames = (
  node: Node = { node_type: EXIT_TYPE }
): { childrenNames: Array<string>, parentProps: Array<string> } => {
  let childrenNames = [];
  let parentProps = [];
  const { node_type: nodeType } = node;
  switch (nodeType) {
    case Types.ENTRY_TYPE:
    case Types.DELAY_TYPE:
      childrenNames = [node.next || ''];
      parentProps = ['next'];
      break;
    case Types.EVENT_LISTENER_TYPE:
      childrenNames = [node.on_seen || '', node.on_expired || ''];
      parentProps = ['on_seen', 'on_expired'];
      break;
    case Types.MESSAGE_TYPE:
      childrenNames = [node.on_sent || '', node.on_not_sent || ''];
      parentProps = ['on_sent', 'on_not_sent'];
      break;
    default:
  }
  return {
    childrenNames: childrenNames.map(name => `${name}`),
    parentProps
  };
};

export const getAllChildNames = (
  nodes: ApiNodes,
  nodeName: string
): Array<string> => {
  const childNames = [];
  const visited = [nodeName];
  while (visited.length > 0) {
    const currentChild = visited.pop();
    childNames.push(`${currentChild}`);
    const childProps = getNodeChildNames(nodes[currentChild]);
    visited.push(...childProps.childrenNames);
  }
  return childNames;
};

export const isLeafNode = (nodes: ApiNodes, node: string): boolean => {
  const childNames = getAllChildNames(nodes, node);
  childNames.shift(); // remove node from childNames list
  return !childNames.some(
    n => n in nodes && RELEVANT_NODE_TYPES.indexOf(nodes[n].node_type) > -1
  );
};

export const getChildNamesExludingBranch = (
  nodes: ApiNodes,
  nodeName: string,
  branch: string
): Array<string> => {
  const childProps = getNodeChildNames(nodes[nodeName]);
  const childPropsToDelete = childProps.parentProps.filter(
    prop => prop !== branch
  );
  const allNodesToDelete = [`${nodeName}`];
  childPropsToDelete.forEach(childName => {
    const childNames = getAllChildNames(nodes, nodes[nodeName][childName]);
    allNodesToDelete.push(...childNames);
  });
  return allNodesToDelete;
};

export const getParentName = (nodes: ApiNodes, nodeName: string): string => {
  const parentNames = []; // TODO: handling multiple parents, can take out if no convergence
  const nodeNames = Object.keys(nodes);
  nodeNames.forEach(n => {
    const childProps = getNodeChildNames(nodes[n]);
    if (childProps.childrenNames.indexOf(`${nodeName}`) > -1) {
      parentNames.push(n);
    }
  });
  return parentNames[0] || '';
};

const getParentsChildPropName = (
  nodes: ApiNodes,
  parentName: string
): Array<string> => {
  const parentChildProps = getNodeChildNames(nodes[parentName]);
  const { parentProps } = parentChildProps;
  return parentProps;
};

const assignParentNewChild = (
  nodes: ApiNodes,
  nodeName: string,
  newName: ?string
): ApiNodes => {
  const parentName = getParentName(nodes, nodeName);
  const parentsChildPropNames = getParentsChildPropName(nodes, parentName);
  const parentsChildPropName = parentsChildPropNames.find(
    n => `${nodes[parentName][n]}` === `${nodeName}`
  );

  const newTree = { ...nodes };

  if (parentName !== '') {
    const maxId = Math.max(...Object.keys(nodes).map(Number));
    const newChildName = newName == null ? maxId + 1 : +newName;
    newTree[parentName][parentsChildPropName] = newChildName;
    if (!(newChildName in nodes)) {
      newTree[newChildName] = { node_type: EXIT_TYPE };
    }
  }
  return newTree;
};

export const pruneNodes = (
  reparentedNodes: ApiNodes,
  allNodesToDelete: Array<string>
): ApiNodes =>
  Object.keys(reparentedNodes)
    .filter(k => !allNodesToDelete.includes(k))
    .reduce(
      (newNodes, key) => ({ ...newNodes, [key]: reparentedNodes[key] }),
      {}
    );

export const deleteNode = (nodes: ApiNodes, nodeName: string): ApiNodes => {
  const { childrenNames } = getNodeChildNames(nodes[nodeName]);
  const reparentedNodes = assignParentNewChild(
    nodes,
    nodeName,
    childrenNames[0]
  );
  return pruneNodes(reparentedNodes, [
    `${nodeName}`,
    ...childrenNames.slice(1)
  ]);
};

export const deleteBranchesExceptOne = (
  nodes: ApiNodes,
  nodeName: string,
  branchNameToKeep: string
): ApiNodes => {
  const allNodesToDelete = getChildNamesExludingBranch(
    nodes,
    nodeName,
    branchNameToKeep
  );

  const reparentedNodes = assignParentNewChild(
    nodes,
    nodeName,
    nodes[nodeName][branchNameToKeep]
  );
  return pruneNodes(reparentedNodes, allNodesToDelete);
};

export const deleteNodeAndAllChildren = (
  nodes: ApiNodes,
  nodeName: string
): ApiNodes => {
  const childNames = getAllChildNames(nodes, nodeName);
  const reparentedNodes = assignParentNewChild(nodes, nodeName);
  return pruneNodes(reparentedNodes, childNames);
};

export const downloadPerformanceCSV = async (
  orgId: number,
  orchId: number,
  orchName: string
) => {
  if (orgId && orchId) {
    const endpoint = `${ORCH_ENDPOINT_PATH}/v1/orchestration/organization/${
      orgId
    }/orchestration/${orchId}/performance`;
    const filename = orchName ? `${orchName}.csv` : 'export.csv';

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Accepts: 'text/csv',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      });

      if (await response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        if (document.body) {
          document.body.appendChild(link);
        }
        link.click();
        if (document.body) {
          document.body.removeChild(link);
        }
      }
    } catch (e) {
      alert('Could not download performance data.');
    }
  }
};



// WEBPACK FOOTER //
// ./src/util/ApiUtils.js